import {
  Component,
  OnInit,
  Input,
  EventEmitter,
  Output,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MicarteraService } from '../../services/micartera.service';
import { CustomDatePickerDirective } from '../../directives/custom-date-picker.directive';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

//Interfaces de Mi cartera
import {
  PolicyLookupResponse,
  ProducerLookupResponse,
} from '../../interfaces/micartera.interfaces';

//Interfaces en común
import {
  ApiResponse,
  LobWithSLobs,
  SLob,
  Currency,
  DocumentType,
} from '../../interfaces/common.interfaces';

//Interfaces de filtros
import {
  CarteraFilter,
  ProduccionFilter,
  RenovacionesFilter,
  CancelacionesFilter,
  CotizadoresFilter,
  ClientPaymentFilter,
  AgingFilter,
  ClientPaymentStatusFilter,
  ReclamosFilter,
  SinestralidadFilter,
  ComisionesFilter,
  ReembolsosFilter,
  DownloadFilter,
} from '../../interfaces/filters.interfaces';

//Interfaces de comisiones
import {
  ComisionesDocumentType,
  TipoDocumentoComisiones,
} from '../../interfaces/comisiones.interface';

// Identificación
import {
  ClientSearchRequest,
  IdentificacionService,
} from '../../services/identificacion.service';
import { AuthService } from '../../services/auth.service';
import { GoogleAnalyticsService } from '../../services/google-analytics/google-analytics.service';

/**
 * Componente para manejar campos de formulario y filtros
 * @description Maneja la lógica de campos de formulario para diferentes tipos de consultas y filtros
 */
@Component({
  selector: 'app-campos',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CustomDatePickerDirective,
  ],
  templateUrl: './campos.component.html',
  styleUrls: ['./campos.component.css'],
})
export class CamposComponent implements OnInit, OnDestroy {
  @Input() camposVisibles: string[] = [];
  @Input() camposObligatorios: string[] = []; // Array con los nombres de los campos obligatorios
  @Input() tipoDoc: string = '';
  @Input() tipo:
    | 'cartera'
    | 'produccion'
    | 'renovaciones'
    | 'cancelaciones'
    | 'cotizadores'
    | 'cobros'
    | 'antiguedad'
    | 'estadoCuentas'
    | 'reclamos'
    | 'reembolsos'
    | 'sinestralidad'
    | 'descargas'
    | 'comisiones' = 'cartera';
  @Input() rangeInMonths: number = 0;
  @Input() defaultDesde: string = '';
  @Input() defaultHasta: string = '';
  @Input() applyDateRangeLimit: boolean = true;

  @Input() sLobName = ''
  @Input() lobName = ''
  @Input() documentTypeName = ''

  @Output() filterChange = new EventEmitter<
    | CarteraFilter
    | ProduccionFilter
    | RenovacionesFilter
    | CancelacionesFilter
    | CotizadoresFilter
    | ClientPaymentFilter
    | AgingFilter
    | ClientPaymentStatusFilter
    | ReclamosFilter
    | SinestralidadFilter
    | ComisionesFilter
    | ReembolsosFilter
    | DownloadFilter
  >();

  /** Evento que se emite cuando se limpian los campos */
  @Output() onClear = new EventEmitter<void>();

  // Form controls
  selectedLob = '';
  selectedSLob = '';
  cliente = '';
  tipoBusquedaCliente = '';
  corte = '';
  desde = '';
  hasta = '';
  moneda = '';
  AllCheck = false;
  onlyPcyWithBalance = false;
  bolanteDePago = false;
  cortePeriodo = '';
  cortePeriodoOption = '';
  corteSinestralidad = '';
  tipoDocumentoSeleccionado: number = 0;
  tipoDocumentoSeleccionadoName: string = ''
  documentoIdentidad: string = '';
  tipoFiltroSeleccionado: string = '';
  busquedaFiltro: string = '';
  policy: string = '';
  eventNumber: string = '';
  tooltipMessage = 'Filtrar por Cédula, RNC (sin guiones ni espacios) o Nombre.'
  tooltipMessageReclamos = 'Filtrar por Cédula, RNC, Reclamo, Póliza (sin guiones ni espacios) o Nombre.'

  selectFilterKeys: Record<string, string> = {
    poliza: 'policyNumber',
    codigo: 'clientCode',
    reclamo: 'eventNumber'
  }

  // Validación de selección de fechas futuras en filtros de fecha
  @Input()
  allowFutureDates = false;

  // Identificación
  clientCode?: number;

  // Selecion comisioens
  SeleccionCortes: string[] = [];

  // Lookups
  lobs: LobWithSLobs[] = [];
  filteredSLobs: SLob[] = [];
  documentTypes: DocumentType[] = [];
  currencies: Currency[] = [];

  // Tipos de documentos de comisiones
  comisionesDocType: ComisionesDocumentType[] = [
    {
      documentTypeId: TipoDocumentoComisiones.VOLANTE_PAGO,
      documentTypeName: 'Volante pago de comisiones',
    },
    {
      documentTypeId: TipoDocumentoComisiones.DETALLES,
      documentTypeName: 'Detalles de comisiones',
    },
    {
      documentTypeId: TipoDocumentoComisiones.DETALLES_POR_COBRO,
      documentTypeName: 'Detalles de comisiones por cobro',
    },
  ];

  // Agregar esta propiedad para rastrear el campo inicial
  campoInicial: 'desde' | 'hasta' | null = null;
  noEncontrado: boolean = false;

  private destroy$ = new Subject<void>();
  private subscription: Subscription;

  /**
   * Constructor del componente
   * @param micarteraService Servicio para operaciones de cartera
   * @param authService Servicio de autenticación
   * @param identificacionService Servicio de identificación de clientes
   */
  constructor(
    private micarteraService: MicarteraService,
    private authService: AuthService,
    private identificacionService: IdentificacionService,
    private gAnalytics:GoogleAnalyticsService
  ) {
    this.clientCode = this.identificacionService.getClientCode() || undefined;
    this.initializeSeleccionCortes();
    this.subscription = this.identificacionService.noEncontrado$.subscribe(
      (noEncontrado) => {
        this.noEncontrado = noEncontrado;
      }
    );
  }

  /**
   * Método del ciclo de vida para limpiar suscripciones
   */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /**
   * Inicializa los cortes de selección según el tipo de documento
   */
  private initializeSeleccionCortes(): void {
    const tipoDoc = this.tipoDoc ? parseInt(this.tipoDoc) : undefined;

    if (tipoDoc === TipoDocumentoComisiones.VOLANTE_PAGO) {
      this.SeleccionCortes = ['Primer corte', 'Segundo corte'];
    } else if (tipoDoc === TipoDocumentoComisiones.DETALLES) {
      this.SeleccionCortes = ['Primer corte', 'Segundo corte', 'Corte mensual'];
    } else if (tipoDoc === TipoDocumentoComisiones.DETALLES_POR_COBRO) {
      this.SeleccionCortes = ['Corte mensual'];
    }
  }

  /**
   * Método del ciclo de vida para inicialización
   */
  ngOnInit() {
    this.applyDefaultDates();
    this.loadLookups();
  }

  /**
   * Aplica las fechas por defecto si fueron recibidas como input
   */
  private applyDefaultDates(): void {
    if (this.defaultDesde && this.camposVisibles.includes('desde')) {
      this.desde = this.defaultDesde;
    }
    if (this.defaultHasta && this.camposVisibles.includes('hasta')) {
      this.hasta = this.defaultHasta;
    }
  }

  /**
   * Carga los lookups según el tipo de consulta
   */
  private loadLookups() {
    switch (this.tipo) {
      //Cartera

      //Mi cartera
      case 'cartera':
      case 'cotizadores':
      case 'estadoCuentas':
      //Recibos de cobros
      case 'cobros':
      //Renovaciones
      case 'renovaciones':
      //Reclamaciones
      case 'reclamos':
        this.loadPolicyLookups();
        break;
      //Producción
      case 'produccion':
        this.loadProductionLookups();
        break;

      //Comisiones
      case 'comisiones':
        this.loadComisiones();
        this.loadPolicyLookups();
        break;
    }
  }

  /**
   * Carga los lookups de pólizas
   */
  private loadPolicyLookups(): void {
    this.micarteraService.getPolicyLookups().subscribe({
      next: (response: ApiResponse<PolicyLookupResponse>) => {
        if (response?.data?.lobs) {
          this.lobs = response.data.lobs;
          if (this.tipo === 'cotizadores') {
            this.lobs = this.lobs.filter(
              (lob) => lob.lobId === 50 || lob.lobId === 70 || lob.lobId === 16
            );
          }
        }
      },
      error: (error) => console.error('Error cargando lookups:', error),
    });
  }

  /**
   * Carga los lookups de producción
   */
  private loadProductionLookups(): void {
    this.micarteraService.getProductionLookups().subscribe({
      next: (response: ApiResponse<ProducerLookupResponse>) => {
        if (response?.data) {
          this.lobs = response.data.lobs;
          this.documentTypes = response.data.documentTypes;
          this.currencies = response.data.currencies;
        }
      },
      error: (error) =>
        console.error('Error cargando lookups de producción:', error),
    });
  }

  /**
   * Carga los tipos de documentos de comisiones
   */
  private loadComisiones(): void {
    this.documentTypes = this.comisionesDocType.map((doc) => ({
      documentTypeId: +doc.documentTypeId,
      documentTypeName: doc.documentTypeName,
    }));
  }

  /**
   * Maneja el cambio de tipo de documento
   */
  onTipoDocChange(): void {
    if (!this.tipoDoc || this.tipoDoc === '' || this.tipoDoc === 'Documentos') {
      this.cortePeriodoOption = '';
    }
    this.initializeSeleccionCortes();
  }

  /**
   * Verifica si el select de corte debe estar deshabilitado
   */
  disableSelectCorte(): boolean {
    return (
      !this.tipoDoc || this.tipoDoc === '' || this.tipoDoc === 'Documentos'
    );
  }

  /**
   * Maneja el cambio de ramo (LOB)
   * @param lobId ID del ramo seleccionado
   */
  onLobChange(lobId: number): void {
    this.selectedSLob = '';
    if (lobId) {
      const selectedLob = this.lobs.find((lob) => lob.lobId === lobId);
      this.filteredSLobs = selectedLob ? selectedLob.sLobs : [];
    } else {
      this.filteredSLobs = [];
    }
  }

  /**
   * Valida los caracteres permitidos en el input
   * @param event Evento del teclado
   * @returns boolean indicando si el caracter es válido
   */
  onValidarKeyPress(event: KeyboardEvent): boolean {
    const pattern = /[0-9-]/;
    const inputChar = String.fromCharCode(event.charCode);

    if (!pattern.test(inputChar)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  /**
   * Maneja el cambio en la fecha desde
   */
  onDesdeChange(): void {
    if (this.campoInicial === 'desde') {
      const fechaDesde = new Date(this.desde);
      const fechaHasta = new Date(fechaDesde);
      fechaHasta.setFullYear(fechaHasta.getFullYear() + 1);
      this.hasta = fechaHasta.toISOString().split('T')[0];
    }
  }

  /**
   * Maneja el cambio en la fecha hasta
   */
  onHastaChange(): void {
    if (this.campoInicial === 'hasta') {
      const fechaHasta = new Date(this.hasta);
      const fechaDesde = new Date(fechaHasta);
      fechaDesde.setFullYear(fechaDesde.getFullYear() - 1);
      this.desde = fechaDesde.toISOString().split('T')[0];
    }
  }

  /**
   * Maneja el cambio en el corte de siniestralidad
   */
  onCorteSinestralidadChange(): void {
    if (this.corteSinestralidad) {
      const dateTo = new Date(this.corteSinestralidad);

      const dateFrom = new Date(dateTo);
      dateFrom.setFullYear(dateTo.getFullYear() - 1);
      dateFrom.setDate(dateTo.getDate() + 1);

      this.desde = dateFrom.toISOString().split('T')[0];
      this.hasta = dateTo.toISOString().split('T')[0];
    }
  }

  /**
   * Inicia la búsqueda según los criterios seleccionados
   */
  buscar(): void {
    if (this.isBuscarDisabled()) return;
    if (['cedula', 'rnc', 'nombre'].includes(this.tipoFiltroSeleccionado)) {
      this.identificacion();
    }
    else if (this.cliente) {
      this.identificacion();
    }
    else if (this.documentoIdentidad) {
      this.reembolsos();
    } else {
      this.emitirCambios();
    }
  }

  /**
   * Emite los cambios en los filtros
   */
  emitirCambios(): void {
    const filtros = this.construirFiltros();
    if (filtros) {
       if (this.tipo !== 'reembolsos') {
        this.trackBusqueda();
      }
      this.filterChange.emit(filtros);
    }
  }

  /**
   * Realiza la identificación del cliente
   */
  identificacion(): void {
    if (this.cliente || this.busquedaFiltro) {
      const request: ClientSearchRequest = {
        IdNumber: this.cliente || this.busquedaFiltro,
        Name: this.cliente || this.busquedaFiltro,
        ProducerNumber: this.authService.getProducerNumber() ?? 0,
      };

      this.identificacionService.searchClient(request).subscribe({
        next: (response: any) => {
          if (response?.data?.clientcode) {
            this.clientCode = response.data.clientcode;
            setTimeout(() => this.emitirCambios(), 0);
          } else {
            console.log('No se encontró código de cliente');
            this.clientCode = undefined;
          }
        },
        error: (error: any) => {
          console.error('Error en búsqueda:', error);
          this.clientCode = undefined;
        },
      });
    }
  }

  /**
   * Continúa después de no encontrar resultados
   */
  onContinue(): void {
    this.isClosing = true;
    setTimeout(() => {
      this.noEncontrado = false;
      this.isClosing = false;
    }, 300);
  }

  /**
   * Maneja la búsqueda de reembolsos
   */
  reembolsos(): void {
    if (this.documentoIdentidad) {
      const tipoDoc = Number(this.tipoDocumentoSeleccionado);
      this.tipoDocumentoSeleccionadoName = this.getTipoDocumentoName(tipoDoc);
        const filters: ReembolsosFilter = {
        idType: this.tipoDocumentoSeleccionado,
        IdNumber: this.documentoIdentidad.replace(/-/g, ''),
      };
      // Trackear en Google Analytics antes de emitir
      this.trackBusqueda();
      this.filterChange.emit(filters);
    }
  }


  isPolicyOrEventNumber(value: string) {
    const containsOnlyNumbers = /^\d+$/.test(value.trim());
    if (containsOnlyNumbers && (value.length > 5 && value.length < 9)) {
      this.eventNumber = value.trim();
      this.policy = '';
      return true
    };
    if (containsOnlyNumbers && (value.length === 12)) {
      this.policy = value.trim();
      this.eventNumber = '';
      return true
    };
    this.policy = '';
    this.eventNumber = '';
    return false;
  }

  /**
   * Construye los filtros según el tipo de consulta
   * @returns Objeto con los filtros construidos
   */
  private construirFiltros():
    | CarteraFilter
    | ProduccionFilter
    | RenovacionesFilter
    | CancelacionesFilter
    | CotizadoresFilter
    | ClientPaymentFilter
    | AgingFilter
    | ClientPaymentStatusFilter
    | ReclamosFilter
    | ReembolsosFilter
    | SinestralidadFilter
    | DownloadFilter
    | ComisionesFilter {
    let baseFilter = {
      lobId: this.selectedLob ? +this.selectedLob : undefined,
      sLobId: this.selectedSLob ? +this.selectedSLob : undefined,
      clientCode: this.clientCode,
    };

    const key = this.selectFilterKeys[this.tipoFiltroSeleccionado];

    if (this.busquedaFiltro) {
      baseFilter = { ...baseFilter, [key]: this.busquedaFiltro }
    }
    switch (this.tipo) {
      case 'cartera':
        return {
          ...baseFilter,
          dateTo: this.corte ? new Date(this.corte) : undefined,
        } as CarteraFilter;

      case 'produccion':
        return {
          ...baseFilter,
          documentType: this.tipoDoc ? +this.tipoDoc : undefined,
          currency: this.moneda ? +this.moneda : undefined,
          dateFrom: this.desde ? new Date(this.desde) : undefined,
          dateTo: this.hasta ? new Date(this.hasta) : undefined,
        } as ProduccionFilter;

      case 'renovaciones':
        return {
          ...baseFilter,
          dateFrom: this.desde ? new Date(this.desde) : undefined,
          dateTo: this.hasta ? new Date(this.hasta) : undefined,
          onlyPcyWithBalance: this.onlyPcyWithBalance,
        } as RenovacionesFilter;

      case 'cancelaciones':
        return {
          ...baseFilter,
          dateFrom: this.desde ? new Date(this.desde) : undefined,
          dateTo: this.hasta ? new Date(this.hasta) : undefined,
        } as CancelacionesFilter;

      case 'cobros':
        return {
          ...baseFilter,
          dateFrom: this.desde ? new Date(this.desde) : undefined,
          dateTo: this.hasta ? new Date(this.hasta) : undefined,
          lob: this.selectedLob ? +this.selectedLob : undefined,
          slob: this.selectedSLob ? +this.selectedSLob : undefined,
        } as ClientPaymentFilter;

      case 'cotizadores':
        return {
          ...baseFilter,
          dateFrom: this.desde ? new Date(this.desde) : undefined,
          dateTo: this.hasta ? new Date(this.hasta) : undefined,
        } as CotizadoresFilter;

      case 'antiguedad':
        return {
          ...baseFilter,
        } as AgingFilter;

      case 'estadoCuentas':
        return {
          ...baseFilter,
          Date: this.corte ? new Date(this.corte) : undefined,
          paidInvoices: this.bolanteDePago,
        } as ClientPaymentStatusFilter;

      case 'reclamos':
        return {
          ...baseFilter,
          dateFrom: this.desde ? new Date(this.desde) : undefined,
          dateTo: this.hasta ? new Date(this.hasta) : undefined,
          EventNumber: parseInt(this.eventNumber),
        } as ReclamosFilter;

      case 'reembolsos':
        return {
          ...baseFilter,
          tipoDocumento: this.tipoDocumentoSeleccionado,
          documentoIdentidad: this.documentoIdentidad,
        } as ReembolsosFilter;

      case 'sinestralidad':
        return {
          ...baseFilter,
          DateFrom: this.desde ? new Date(this.desde) : undefined,
          DateTo: this.hasta ? new Date(this.hasta) : undefined,
        } as SinestralidadFilter;

      case 'descargas':
        return {
          ...baseFilter,
          dateFrom: this.desde ? new Date(this.desde) : undefined,
          dateTo: this.hasta ? new Date(this.hasta) : undefined,
          Policy: this.policy ? +this.policy : undefined,
        } as DownloadFilter;

      case 'comisiones':
        return {
          ...baseFilter,
          cutDate: this.cortePeriodo ? new Date(this.cortePeriodo) : undefined,
          cutSelection: this.cortePeriodoOption
            ? +this.cortePeriodoOption
            : undefined,
          tipoDoc: this.tipoDoc ? +this.tipoDoc : undefined,
        } as ComisionesFilter;
    }
  }

  /**
   * Limpia todos los campos del formulario
   */
  limpiar(): void {
    this.selectedLob = '';
    this.selectedSLob = '';
    this.cliente = '';
    this.clientCode = undefined;
    this.tipoBusquedaCliente = '';
    this.corte = '';
    this.desde = this.defaultDesde || '';
    this.hasta = this.defaultHasta || '';
    this.tipoDoc = '';
    this.moneda = '';
    this.onlyPcyWithBalance = false;
    this.bolanteDePago = false;
    this.filteredSLobs = [];
    this.AllCheck = false;
    this.cortePeriodo = '';
    this.cortePeriodoOption = '';
    this.filterChange.emit({});
    this.campoInicial = null;
    this.corteSinestralidad = '';
    this.tipoDocumentoSeleccionado = 0;
    this.documentoIdentidad = '';
    this.policy = '';
    this.noEncontrado = false;
    this.onClear.emit();
    this.eventNumber = '';
    this.policy = '';
    this.tipoFiltroSeleccionado = '';
    this.busquedaFiltro = '';
  }

  /**
   * Maneja el evento de focus en campos
   */
  onFocus(): void {
    this.resetFields();
  }

  /**
   * Resetea los campos de cliente
   */
  resetFields(): void {
    this.documentoIdentidad = '';
    this.cliente = '';
    this.clientCode = undefined;
  }

  /**
   * Verifica si un campo es obligatorio según la configuración y el filtro activo
   * @param campo Nombre del campo a validar
   * @returns boolean indicando si el campo es obligatorio
   */
  isRequiredField(campo: string): boolean {
    if (!this.camposObligatorios.includes(campo)) {
      return false;
    }

    if (
      this.tipo === 'reclamos' &&
      this.tipoFiltroSeleccionado === 'reclamo' &&
      (campo === 'desde' || campo === 'hasta')
    ) {
      return false;
    }

    return true;
  }

  /**
   * Verifica si el botón de búsqueda debe estar deshabilitado
   * @returns boolean indicando si el botón debe estar deshabilitado
   */
  isBuscarDisabled(): boolean {
    if (this.tipoBusquedaCliente && !this.cliente) {
      return true;
    }

    switch (this.tipo) {
      case 'cartera':
        return !this.corte || !this.isValidDateRange();
      case 'produccion':
      case 'renovaciones':
      case 'cancelaciones':
      case 'reclamos': {
        const searchByClaim = this.tipoFiltroSeleccionado === 'reclamo';
        const hasClaimNumber = searchByClaim && !!this.busquedaFiltro;
        const hasIdentifierLegacy = !!this.policy || !!this.eventNumber;
        const completeAndValidDates =
          !!this.desde && !!this.hasta && this.isValidDateRange();

        if (hasClaimNumber || hasIdentifierLegacy) {
          return false;
        }

        if (searchByClaim) {
          return !this.busquedaFiltro;
        }

        return !completeAndValidDates;
      }
      case 'cotizadores':
        return (
          !this.desde ||
          !this.hasta ||
          !this.selectedLob ||
          !this.selectedSLob ||
          !this.isValidDateRange()
        );
      case 'cobros':
        return !this.desde || !this.hasta || !this.isValidDateRange();
      case 'estadoCuentas':
        return !this.corte || !this.busquedaFiltro || !this.isValidDateRange();
      case 'antiguedad':
        return !this.busquedaFiltro && this.AllCheck;
      case 'sinestralidad':
        return !this.desde || !this.hasta || !this.isValidDateRange();
      case 'descargas':
        return !this.desde || !this.hasta;
      case 'comisiones':
        return !this.tipoDoc || !this.cortePeriodo || !this.cortePeriodoOption;

      case 'reembolsos':
        return (
          !this.documentoIdentidad ||
          !this.tipoDocumentoSeleccionado ||
          !this.isValidDocumentLength()
        );
      default:
        return false;
    }
  }

  //Fecha máxima para renovación
  fechaMaxima: string = '31';

  /**
   * Valida el rango de fechas seleccionado
   * @returns boolean indicando si el rango es válido
   */
  isValidDateRange(): boolean {
    if (!this.desde || !this.hasta) return true;

    const fechaDesde = new Date(this.desde);
    const fechaHasta = new Date(this.hasta);

    if (fechaDesde > fechaHasta) return false;

    return true;
  }

  /**
   * Obtiene la fecha máxima permitida
   * @param rangeInMonths Número de meses permitido desde la fecha mínima
   * @returns string con la fecha máxima en formato ISO
   */
  getMaxDate(): string {
    const fechaActual = new Date();
    fechaActual.setHours(0, 0, 0, 0);

    if (!this.applyDateRangeLimit) {
      if (this.allowFutureDates) {
        return '';
      }

      return fechaActual.toISOString().split('T')[0];
    }

    if (!this.desde && this.hasta) {
      return this.hasta;
    }

    if (this.desde) {
      const fechaDesde = new Date(this.desde);
      const fechaMaxima = new Date(fechaDesde);
      fechaMaxima.setMonth(
        fechaMaxima.getMonth() + (this.rangeInMonths ?? 0)
      );
      if (!this.rangeInMonths) {
        // fallback de días si no se pasa rangeInMonths
        fechaMaxima.setDate(fechaMaxima.getDate() + 60);
      }

      // Permite selección de fechas futuras
      if (this.allowFutureDates) {
        return fechaMaxima.toISOString().split('T')[0];
      }

      return fechaMaxima < fechaActual
        ? fechaMaxima.toISOString().split('T')[0]
        : fechaActual.toISOString().split('T')[0];
    }

    // Permite selección de fechas futuras
    if (this.allowFutureDates) return '';

    return fechaActual.toISOString().split('T')[0];
  }

  /**
   * Obtiene la fecha mínima permitida
   * @param rangeInMonths Número de meses permitido hacia atrás desde la fecha máxima
   * @returns string con la fecha mínima en formato ISO
   */
  getMinDate(): string {
    if (!this.applyDateRangeLimit) {
      return '';
    }

    if (this.hasta) {
      const fechaHasta = new Date(this.hasta);
      const fechaMinima = new Date(fechaHasta);
      fechaMinima.setMonth(
        fechaMinima.getMonth() - (this.rangeInMonths ?? 0)
      );
      if (!this.rangeInMonths) {
        // fallback de días si no se pasa rangeInMonths
        fechaMinima.setDate(fechaMinima.getDate() - 60);
      }

      return fechaMinima.toISOString().split('T')[0];
    }

    return '';
  }

  /**
   * Maneja el cambio en el tipo de búsqueda
   */
  onTipoBusquedaChange(): void {
    if (this.tipoBusquedaCliente) {
      this.cliente = '';
    } else {
      this.cliente = '';
    }
  }

  /**
   * Obtiene la etiqueta para el tipo de búsqueda
   * @returns string con la etiqueta correspondiente
   */
  getTipoBusquedaLabel(): string {
    switch (this.tipoBusquedaCliente) {
      case 'cedula':
        return 'Escriba la Cédula/RNC';
      case 'poliza':
        return 'Escriba la Póliza';
      default:
        return 'Seleccione tipo de búsqueda';
    }
  }

  // longitud de los tipos de documento de identificacion
  maxLengths = {
    RNC: 11,
    CEDULA: 11,
    PASAPORTE: 100,
    POLIZA: 12
  };

  // tipos de documento de identificacion
  tiposDocumentoIdentificacion = [
    { id: '1', nombre: 'RNC' },
    { id: '2', nombre: 'Cédula' },
    { id: '3', nombre: 'Pasaporte' },
  ];

  // opciones de identificacion
  @Input()
  opcionesSelectFilter: { id: string, nombre: string }[] = [];

  /**
   * Maneja el evento keypress para validar caracteres
   * @param event Evento del teclado
   */
  keypress(event: KeyboardEvent): void {
    // Si es una tecla de navegación, permitirla
    if (
      event.key === 'Backspace' ||
      event.key === 'Delete' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'Tab'
    ) {
      return;
    }

    const tipoDoc = Number(this.tipoDocumentoSeleccionado) || this.tipoFiltroSeleccionado;
    switch (tipoDoc) {
      case 1: // RNC
        if (
          !/^\d$/.test(event.key) ||
          this.documentoIdentidad.length >= this.maxLengths.RNC
        ) {
          event.preventDefault();
        }
        break;

      case 2: // Cédula
        if (
          !/^\d$/.test(event.key) ||
          this.documentoIdentidad.length >= this.maxLengths.CEDULA
        ) {
          event.preventDefault();
        }
        break;

      case 3: // Pasaporte
        if (
          !/^[a-zA-Z0-9]$/.test(event.key) ||
          this.documentoIdentidad.length >= this.maxLengths.PASAPORTE
        ) {
          event.preventDefault();
        }
        break;
      case 'cedula':
        if (
          !/^\d$/.test(event.key) ||
          this.busquedaFiltro.length >= this.maxLengths.CEDULA
        ) {
          event.preventDefault();
        }
        break;
      case 'rnc':
        if (
          !/^\d$/.test(event.key) ||
          this.busquedaFiltro.length >= this.maxLengths.RNC
        ) {
          event.preventDefault();
        }
        break;
      case 'poliza':
        if (
          !/^\d$/.test(event.key) ||
          this.busquedaFiltro.length >= this.maxLengths.POLIZA
        ) {
          event.preventDefault();
        }
        break;
      case 'nombre':
      case 'codigo':
      case 'reclamo':
        break;

      default:
        event.preventDefault();
        break;
    }
  }

  /**
   * Maneja los cambios en el input
   * @param event Evento de cambio
   */
  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    const tipoDoc = Number(this.tipoDocumentoSeleccionado);
    switch (tipoDoc) {
      case 1: // RNC
        value = value.replace(/[^\d]/g, '').slice(0, this.maxLengths.RNC);
        break;

      case 2: // Cédula
        // Primero removemos cualquier guión existente y limitamos la longitud
        value = value.replace(/[^\d]/g, '').slice(0, this.maxLengths.CEDULA);

        if (value.length > 0) {
          // Guardamos el valor sin guiones en this.documentoIdentidad
          this.documentoIdentidad = value;

          // Solo para mostrar en el input, aplicamos el formato con guiones
          input.value = value.replace(/(\d{3})(\d{7})(\d{1})/, '$1-$2-$3');

          // Prevenimos que el evento normal actualice el input
          event.preventDefault();
        }
        break;

      case 3: // Pasaporte
        value = value
          .replace(/[^a-zA-Z0-9]/g, '')
          .toUpperCase()
          .slice(0, this.maxLengths.PASAPORTE);
        break;
    }

    this.documentoIdentidad = value;
  }
  /**
   * Maneja los cambios en el input
   * @param event Evento de cambio
   */
  onInputFilterChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    const tipoDoc = this.tipoFiltroSeleccionado;
    switch (tipoDoc) {
      case 'rnc': // RNC
        value = value.replace(/[^\d]/g, '').slice(0, this.maxLengths.RNC);
        break;

      case 'cedula': // Cédula
        // Primero removemos cualquier guión existente y limitamos la longitud
        value = value.replace(/[^\d]/g, '').slice(0, this.maxLengths.CEDULA);

        if (value.length > 0) {
          // Guardamos el valor sin guiones en this.documentoIdentidad
          this.busquedaFiltro = value;

          // Solo para mostrar en el input, aplicamos el formato con guiones
          input.value = value.replace(/(\d{3})(\d{7})(\d{1})/, '$1-$2-$3');

          // Prevenimos que el evento normal actualice el input
          event.preventDefault();
        }
        break;
      case 'poliza':
        value = value.replace(/[^\d]/g, '').slice(0, this.maxLengths.POLIZA);
        break;
      case 'codigo':
        value = value.replace(/[^\d]/g, '').slice(0, 7);
        break;
      case 'reclamo':
        value = value.replace(/[^\d]/g, '').slice(0, 7);
        break;
    }

    this.busquedaFiltro = value;
  }

  isClosing = false;

  /**
   * Valida la longitud del documento según su tipo
   * @returns boolean indicando si la longitud es válida
   */
  private isValidDocumentLength(): boolean {
    if (!this.documentoIdentidad) return false;

    const tipoDoc = Number(this.tipoDocumentoSeleccionado) || this.tipoFiltroSeleccionado;
    const length = this.documentoIdentidad.replace(/-/g, '').length;

    switch (tipoDoc) {
      case 1: // RNC
        return length >= 9 && length <= this.maxLengths.RNC;
      case 2: // Cédula
        return length === this.maxLengths.CEDULA;
      case 3: // Pasaporte
        return length > 0 && length <= this.maxLengths.PASAPORTE;
      case 'cedula': // Cédula
        return length === this.maxLengths.CEDULA;
      case 'rnc': // RNC
        return length >= 9 && length <= this.maxLengths.RNC;
      case 'poliza': // Cédula
        return length === this.maxLengths.POLIZA;
      default:
        return false;
    }
  }

  onEnterPress(): void {
    if (!this.isBuscarDisabled()) {
      this.buscar();
    }
  }

  /**
* Trackea el evento de búsqueda en Google Analytics
* @param filtros Filtros aplicados en la búsqueda
*/
  private trackBusqueda(): void {
    const eventName = `busqueda_${this.tipo}`;

    const params: any = {
      page: `/${this.tipo}`,
      action: `Búsqueda realizada en ${this.tipo}`,
      tipo_busqueda: this.tipo,
    };

    // Filtros comunes
    if (this.selectedLob) params.ramo = this.lobName;
    if (this.selectedSLob) params.subramo = this.sLobName;
    if (this.tipoFiltroSeleccionado) params.tipo_filtro = this.tipoFiltroSeleccionado;
    if (this.busquedaFiltro) params.valor_busqueda = this.busquedaFiltro;
    if (this.clientCode) params.codigo_cliente = this.clientCode;

    // Filtros de fechas
    if (this.desde) params.fecha_desde = this.desde;
    if (this.hasta) params.fecha_hasta = this.hasta;
    if (this.corte) params.fecha_corte = this.corte;
    if (this.corteSinestralidad) params.corte_sinestralidad = this.corteSinestralidad;

    // Filtros específicos de producción
    if (this.tipoDoc) params.tipo_documento = this.documentTypeName;
    if (this.moneda) params.moneda = this.moneda;

    // Filtros booleanos
    if (this.onlyPcyWithBalance) params.solo_con_balance = true;
    if (this.bolanteDePago) params.bolante_pago = true;
    if (this.AllCheck) params.todos_seleccionados = true;

    // Filtros de comisiones
    if (this.cortePeriodo) params.corte_periodo = this.cortePeriodo;
    if (this.cortePeriodoOption) params.opcion_corte = this.cortePeriodoOption;

    // Trackear nombre de tipo de documento en comisiones
    if (this.tipo === 'comisiones' && this.tipoDoc) {
      const comisionDoc = this.comisionesDocType.find(
        (d) => +d.documentTypeId === +this.tipoDoc
      );
      if (comisionDoc) {
        params.tipo_documento_comision = comisionDoc.documentTypeName;
      }
    }

    // Filtros de reembolsos
    if (this.tipoDocumentoSeleccionado) params.tipo_documento_id = this.tipoDocumentoSeleccionadoName;
    if (this.documentoIdentidad) params.documento_identidad = this.documentoIdentidad;

    // Filtros de reclamos/descargas
    if (this.policy) params.poliza = this.policy;
    if (this.eventNumber) params.numero_reclamo = this.eventNumber;

    this.gAnalytics.trackEvent(eventName, params);
  }

//Obtiene el nombre del tipo de documento para pasarle el parametro a Google Analytics
  getTipoDocumentoName(tipo: number | string): string {
    switch (tipo) {
      case 1:
        return 'RNC';
      case 2:
        return 'Cédula';
      case 3:
        return 'Pasaporte';
      default:
        return '';
    }
  }
}
